import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tabs,
  Typography,
  Select,
  DatePicker,
  Tag,
  Button,
} from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  DownloadOutlined,
  ShoppingOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const Earnings = () => {
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [period, setPeriod] = useState('month');

  // Update date range when period changes
  const handlePeriodChange = (value) => {
    setPeriod(value);
    switch (value) {
      case 'today':
        setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
        break;
      case 'week':
        setDateRange([dayjs().subtract(7, 'days'), dayjs()]);
        break;
      case 'month':
        setDateRange([dayjs().subtract(30, 'days'), dayjs()]);
        break;
      case 'year':
        setDateRange([dayjs().subtract(365, 'days'), dayjs()]);
        break;
      default:
        setDateRange([dayjs().subtract(30, 'days'), dayjs()]);
    }
  };

  // Fetch earnings data
  const { data: earningsData, isLoading: earningsLoading } = useQuery(
    ['provider-earnings', period, dateRange],
    async () => {
      const response = await api.get('/provider/earnings', {
        params: {
          period: period,
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
        },
      });
      return response.data;
    },
  );

  // Fetch analytics data
  const { data: analyticsData } = useQuery(['provider-analytics', period], async () => {
    const response = await api.get('/provider/analytics', {
      params: { period: period },
    });
    return response.data;
  });

  const earnings = earningsData?.data || earningsData || {};
  const analytics = analyticsData?.data || analyticsData || {};
  const orderAnalytics = analytics.orders || {};
  const customerAnalytics = analytics.customers || {};
  const metrics = analytics.metrics || {};

  // Combine services and products into transactions
  const transactionsData = [
    ...(earnings.services || []),
    ...(earnings.products || []),
    ...(earnings.transactions || []),
  ];

  const transactions = transactionsData.map((item) => {
    const amount = parseFloat(item.total_amount || item.amount || 0);
    return {
      id: item.id,
      type: item.type,
      created_at: item.booking_date || item.created_at,
      order_id: item.id,
      description:
        item.name || item.service_name || item.description || `${item.type}: ${item.name}`,
      amount: isNaN(amount) ? 0 : amount,
      customer_name: item.customer_name,
    };
  });

  // Separate services and products for display
  const servicesData = earnings.services || [];
  const productsData = earnings.products || [];

  const transactionsColumns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
      render: (id) => <Tag>#{id}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <span style={{ color: amount >= 0 ? '#10b981' : '#ff4d4f' }}>
          {amount >= 0 ? '+' : ''}Rs. {Math.abs(amount).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'earning' ? 'green' : type === 'commission' ? 'orange' : 'blue'}>
          {type}
        </Tag>
      ),
    },
  ];

  const ordersColumns = [
    {
      title: 'Booking ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <Tag>#{id}</Tag>,
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Service',
      dataIndex: 'service_name',
      key: 'service_name',
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: 'Date',
      dataIndex: 'booking_date',
      key: 'booking_date',
      render: (date) => (date ? dayjs(date).format('MMM D, YYYY') : '-'),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <Title level={2}>
          <DollarOutlined style={{ marginRight: '8px' }} />
          Earnings & Analytics
        </Title>
        <Button icon={<DownloadOutlined />}>Export Report</Button>
      </div>

      {/* Period Selection */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong>Period:</Text>
          </Col>
          <Col>
            <Select value={period} onChange={handlePeriodChange} style={{ width: 150 }}>
              <Select.Option value="today">Today</Select.Option>
              <Select.Option value="week">This Week</Select.Option>
              <Select.Option value="month">This Month</Select.Option>
              <Select.Option value="year">This Year</Select.Option>
            </Select>
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [dayjs().subtract(30, 'days'), dayjs()])}
            />
          </Col>
        </Row>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={earnings.total_revenue || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#10b981' }}
              precision={2}
              formatter={(value) => `Rs. ${value}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Earnings"
              value={earnings.total_earnings || 0}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#1890ff' }}
              precision={2}
              formatter={(value) => `Rs. ${value}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={earnings.total_orders || 0}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg. Order Value"
              value={earnings.avg_order_value || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#faad14' }}
              precision={2}
              formatter={(value) => `Rs. ${value}`}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts and Tables */}
      <Tabs
        items={[
          {
            key: 'transactions',
            label: (
              <span>
                <DollarOutlined /> Transactions
              </span>
            ),
            children: (
              <Card>
                <Table
                  columns={transactionsColumns}
                  dataSource={transactions}
                  loading={earningsLoading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`,
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'services',
            label: (
              <span>
                <ShoppingOutlined /> Services
              </span>
            ),
            children: (
              <Card>
                <Table
                  columns={ordersColumns}
                  dataSource={servicesData}
                  loading={earningsLoading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} services`,
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'products',
            label: (
              <span>
                <ShoppingOutlined /> Products
              </span>
            ),
            children: (
              <Card>
                <Table
                  columns={ordersColumns}
                  dataSource={productsData}
                  loading={earningsLoading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} products`,
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'analytics',
            label: (
              <span>
                <RiseOutlined /> Analytics
              </span>
            ),
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Card title="Order Status Breakdown">
                    <div style={{ padding: '20px 0' }}>
                      {analytics.order_status_breakdown?.map((item, index) => (
                        <div key={index} style={{ marginBottom: 12 }}>
                          <Row justify="space-between">
                            <Col>
                              <Text>{item.status}</Text>
                            </Col>
                            <Col>
                              <Tag color="blue">{item.count} orders</Tag>
                            </Col>
                          </Row>
                          <div
                            style={{
                              height: 8,
                              background: '#f0f0f0',
                              borderRadius: 4,
                              marginTop: 4,
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${item.percentage}%`,
                                background: '#1890ff',
                                borderRadius: 4,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title="Revenue by Category">
                    {analytics.category_revenue?.map((item, index) => (
                      <div key={index} style={{ marginBottom: 16 }}>
                        <Row justify="space-between">
                          <Col>
                            <Text>{item.category}</Text>
                          </Col>
                          <Col>
                            <Text strong>Rs. {item.revenue.toFixed(2)}</Text>
                          </Col>
                        </Row>
                        <div
                          style={{
                            height: 8,
                            background: '#f0f0f0',
                            borderRadius: 4,
                            marginTop: 4,
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              width: `${item.percentage}%`,
                              background: '#10b981',
                              borderRadius: 4,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </Card>
                </Col>
                <Col xs={24}>
                  <Card title="Performance Metrics">
                    <Row gutter={16}>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title="Completion Rate"
                          value={orderAnalytics.completion_rate || metrics.completion_rate || 0}
                          suffix="%"
                          prefix={<CheckCircleOutlined />}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title="Response Time"
                          value={metrics.avg_response_time || orderAnalytics.avg_response_time || 0}
                          suffix="min"
                          prefix={<ClockCircleOutlined />}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title="Customer Rating"
                          value={customerAnalytics.customer_satisfaction || 0}
                          precision={1}
                          suffix="/ 5"
                          prefix={<UserOutlined />}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Statistic
                          title="Return Rate"
                          value={metrics.return_rate || 0}
                          suffix="%"
                          prefix={<FallOutlined />}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            ),
          },
        ]}
      />
    </div>
  );
};

export default Earnings;
